import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { IoMdClose } from "react-icons/io";
import Btn from "../shared/button/Btn";
import InputCityTown from "../shared/input/new/InputCityTown";
import InputText from "../shared/input/new/InputText";
import InputTextarea from "../shared/input/new/InputTextarea";
import SmallSpinner from "../shared/spinners/SmallSpinner";
import AbsoluteLocationMap from "../map/AbsoluteLocationMap";
import locationData from "../../utils/locationData/locationOptions";
import {
  addDriverSavedAddress,
  updateDriverSavedAddress,
} from "../../features/driverAddress/driverAddressSlice";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.68);

  .dialog {
    width: min(820px, 100%);
    max-height: calc(100vh - 40px);
    overflow: auto;
    padding: 22px;
    border-radius: 12px;
    color: ${({ theme }) => theme.mainTextColor};
    background: ${({ theme }) => theme.mainBgColor};
  }

  .header, .actions, .location-summary {
    display: flex;
    align-items: center;
  }

  .header {
    justify-content: space-between;
    margin-bottom: 22px;
  }

  .header h3 { margin: 0; }
  .close { cursor: pointer; font-size: 24px; }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  .full { grid-column: 1 / -1; }
  .field { display: flex; flex-direction: column; gap: 5px; }
  .error { color: var(--color-main); font-size: 12px; }

  .location-summary {
    justify-content: space-between;
    gap: 15px;
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 8px;
    font-size: 13px;
  }

  .location-text { min-width: 0; overflow-wrap: anywhere; }
  .actions { justify-content: flex-end; gap: 10px; margin-top: 24px; }

  .map-dialog { width: min(900px, 100%); }
  .map-area { width: 100%; height: 480px; }
  .map-address { margin-top: 12px; font-size: 13px; overflow-wrap: anywhere; }

  @media (max-width: 700px) {
    padding: 0;
    .dialog { min-height: 100dvh; max-height: 100dvh; border-radius: 0; }
    .form-grid { grid-template-columns: 1fr; }
    .full { grid-column: auto; }
    .location-summary { align-items: stretch; flex-direction: column; }
    .map-area { height: 60dvh; }
  }
`;

const findLocation = (address) =>
  locationData.find(
    (item) => item.city === address?.city && item.town === address?.town
  ) ?? null;

const createInitialForm = (address) => {
  const location = findLocation(address);
  return {
    title: address?.title ?? "",
    tag: address?.tag ?? "",
    address: address?.address ?? "",
    contactPersonName: address?.contactPersonName ?? "",
    contactPersonPhoneNumber: address?.contactPersonPhoneNumber ?? "",
    locationData: {
      ...(location ?? {}),
      city: address?.city ?? location?.city ?? "",
      town: address?.town ?? location?.town ?? "",
      location:
        location?.location ??
        [address?.town, address?.city].filter(Boolean).join("/"),
      lat: address?.lat ?? "",
      lng: address?.lng ?? "",
      googleAddress: address?.address ?? "",
    },
  };
};

const normalizePhone = (value) => {
  let digits = String(value ?? "").replace(/\D/g, "");
  if (digits.startsWith("90") && digits.length === 12) digits = digits.slice(2);
  if (digits.startsWith("0") && digits.length === 11) digits = digits.slice(1);
  return digits || null;
};

const nullableText = (value) => value?.trim() || null;

const isCoordinate = (value) =>
  value !== "" && value != null && Number.isFinite(Number(value));

const DriverAddressFormModal = ({ targetOwnerUserId, address = null, onClose }) => {
  const dispatch = useDispatch();
  const { isMutating } = useSelector((state) => state.driverAddress);
  const isEditing = Boolean(address?.id);
  const [form, setForm] = useState(() => createInitialForm(address));
  const [submitted, setSubmitted] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapDraft, setMapDraft] = useState(() => createInitialForm(address));
  const mapRef = useRef(null);

  const hasLocation = useMemo(
    () =>
      isCoordinate(form.locationData?.lat) &&
      isCoordinate(form.locationData?.lng),
    [form.locationData?.lat, form.locationData?.lng]
  );

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleLocationChange = (selectedLocation) => {
    setForm((current) => ({
      ...current,
      locationData: {
        ...selectedLocation,
        googleAddress: "",
      },
    }));
  };

  const openMap = () => {
    setMapDraft({
      ...form,
      locationData: { ...form.locationData },
    });
    setMapOpen(true);
  };

  const applyMapLocation = () => {
    if (
      !isCoordinate(mapDraft.locationData?.lat) ||
      !isCoordinate(mapDraft.locationData?.lng)
    ) {
      return;
    }
    const lat = Number(mapDraft.locationData?.lat);
    const lng = Number(mapDraft.locationData?.lng);

    setForm((current) => ({
      ...current,
      address: current.address || mapDraft.locationData?.googleAddress || "",
      locationData: {
        ...current.locationData,
        ...mapDraft.locationData,
        lat: String(lat),
        lng: String(lng),
      },
    }));
    setMapOpen(false);
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!targetOwnerUserId || !form.title.trim() || !hasLocation) return;

    const addressPayload = {
      title: form.title.trim(),
      tag: nullableText(form.tag),
      city: nullableText(form.locationData?.city),
      town: nullableText(form.locationData?.town),
      address: nullableText(form.address),
      lat: String(form.locationData.lat),
      lng: String(form.locationData.lng),
      contactPersonName: nullableText(form.contactPersonName),
      contactPersonPhoneNumber: normalizePhone(form.contactPersonPhoneNumber),
    };

    try {
      if (isEditing) {
        await dispatch(
          updateDriverSavedAddress({
            targetOwnerUserId,
            address: { id: address.id, ...addressPayload },
          })
        ).unwrap();
      } else {
        await dispatch(
          addDriverSavedAddress({
            targetOwnerUserIds: [targetOwnerUserId],
            ...addressPayload,
          })
        ).unwrap();
      }
      onClose();
    } catch (_) {
      // Backend mesajı thunk tarafından toast olarak gösterilir.
    }
  };

  if (mapOpen) {
    return (
      <Overlay onClick={() => setMapOpen(false)}>
        <div className="dialog map-dialog" onClick={(event) => event.stopPropagation()}>
          <div className="header">
            <h3>Haritadan Konum Seç</h3>
            <IoMdClose className="close" onClick={() => setMapOpen(false)} />
          </div>
          <div className="map-area">
            <AbsoluteLocationMap
              mapRef={mapRef}
              updatedLocationItem={mapDraft}
              setUpdatedLocationItem={setMapDraft}
              setSearchText={() => {}}
            />
          </div>
          <div className="map-address">
            {mapDraft.locationData?.googleAddress || "Haritayı seçmek istediğiniz konuma taşıyın."}
          </div>
          <div className="actions">
            <Btn width="130px" color="#E85446" bgColor="transparent" handleClick={() => setMapOpen(false)}>
              Vazgeç
            </Btn>
            <Btn
              width="180px"
              bgColor="#E85446"
              color="#fff"
              isDisabled={
                !isCoordinate(mapDraft.locationData?.lat) ||
                !isCoordinate(mapDraft.locationData?.lng)
              }
              handleClick={applyMapLocation}
            >
              Bu Konumu Kullan
            </Btn>
          </div>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={onClose}>
      <div className="dialog" onClick={(event) => event.stopPropagation()}>
        <div className="header">
          <h3>{isEditing ? "Sürücü Adresini Düzenle" : "Sürücüye Yeni Adres Ekle"}</h3>
          <IoMdClose className="close" onClick={onClose} />
        </div>

        <div className="form-grid">
          <div className="field">
            <InputText data={form.title} setData={(event) => setField("title", event.target.value)} inputWidth="100%" labelText="Başlık*" name="driverAddressTitle" />
            {submitted && !form.title.trim() && <span className="error">Başlık zorunludur.</span>}
          </div>
          <div className="field">
            <InputText data={form.tag} setData={(event) => setField("tag", event.target.value)} inputWidth="100%" labelText="Etiket" name="driverAddressTag" />
          </div>
          <div className="field full">
            <InputCityTown data={form.locationData?.location ?? ""} setData={handleLocationChange} inputWidth="100%" labelText="İlçe/İl" name="driverAddressCityTown" />
          </div>
          <div className="field full">
            <InputTextarea data={form.address} setData={(event) => setField("address", event.target.value)} inputWidth="100%" labelText="Açık Adres" name="driverAddressText" />
          </div>
          <div className="field">
            <InputText data={form.contactPersonName} setData={(event) => setField("contactPersonName", event.target.value)} inputWidth="100%" labelText="Yetkili Kişi" name="driverAddressContactName" />
          </div>
          <div className="field">
            <InputText data={form.contactPersonPhoneNumber} setData={(event) => setField("contactPersonPhoneNumber", event.target.value.replace(/\D/g, "").slice(0, 12))} inputWidth="100%" labelText="Yetkili Telefonu" name="driverAddressContactPhone" />
          </div>
          <div className="field full">
            <div className="location-summary">
              <div className="location-text">
                {hasLocation
                  ? `Konum: ${form.locationData.lat}, ${form.locationData.lng}${form.locationData.googleAddress ? ` — ${form.locationData.googleAddress}` : ""}`
                  : "Henüz bir konum seçilmedi."}
              </div>
              <Btn width="190px" bgColor="transparent" color="#E85446" handleClick={openMap}>
                Haritadan Konum Seç
              </Btn>
            </div>
            {submitted && !hasLocation && <span className="error">Konum seçimi zorunludur.</span>}
          </div>
        </div>

        <div className="actions">
          <Btn width="130px" color="#E85446" bgColor="transparent" handleClick={onClose} isDisabled={isMutating}>
            Vazgeç
          </Btn>
          <Btn width="180px" bgColor="#E85446" color="#fff" handleClick={handleSubmit} isDisabled={isMutating}>
            {isMutating ? <SmallSpinner size="18px" color="#E85446" bgColor="#fff" /> : isEditing ? "Güncelle" : "Adresi Ekle"}
          </Btn>
        </div>
      </div>
    </Overlay>
  );
};

export default DriverAddressFormModal;
